import { Link } from '@mui/material';

import { useNavigate, Link as RouterLink } from 'react-router';

import { useForm } from 'react-hook-form';

import { ClassValidatorResolver } from '@app-velocity/validators';
import { useI18n } from '@app-velocity/i18n';
import {
  Form,
  TextField,
  PageHeader,
  ErrorMessage,
  Button,
} from '@app-velocity/ui';

import { AuthRoutesPaths } from '@app-velocity/libs/auth';

import { UserUpdatePasswordDto } from '../../dto/UserUpdatePasswordDto';
import { useUpdatePasswordMutation } from '../../queries/useUpdatePasswordMutation';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<UserUpdatePasswordDto>({
    resolver: ClassValidatorResolver(UserUpdatePasswordDto),
  });

  const { t } = useI18n();

  const {
    mutate: resetPassword,
    data,
    error,
    isPending,
  } = useUpdatePasswordMutation();

  const onSubmit = (data: UserUpdatePasswordDto) => {
    resetPassword(data);
  };

  const handleSignInClick = () => {
    navigate(AuthRoutesPaths.Login);
  };

  if (data) {
    return (
      <>
        <PageHeader>
          <PageHeader.Title>
            {t('users:reset_password.success')}
          </PageHeader.Title>
          <PageHeader.Subtitle>
            {t('users:reset_password.success_message')}
          </PageHeader.Subtitle>
        </PageHeader>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSignInClick}
        >
          {t('users:auth.sign_in')}
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <PageHeader.Title>{t('users:reset_password.title')}</PageHeader.Title>
        <PageHeader.Subtitle>
          {t('users:reset_password.description')}
        </PageHeader.Subtitle>
      </PageHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.FieldContainer>
          <Form.Label>{t('users:reset_password.new_password')}</Form.Label>
          <TextField
            name="password"
            type="password"
            control={control}
            placeholder={t('users:reset_password.new_password_placeholder')}
            fullWidth
            helperText={formErrors.password?.message}
            error={!!formErrors.password}
          />
        </Form.FieldContainer>

        <Form.FieldContainer>
          <Form.Label>
            {t('users:reset_password.confirm_new_password')}
          </Form.Label>
          <TextField
            name="confirm_password"
            type="password"
            control={control}
            placeholder={t(
              'users:reset_password.confirm_new_password_placeholder'
            )}
            fullWidth
            helperText={formErrors.confirm_password?.message}
            error={!!formErrors.confirm_password}
          />
        </Form.FieldContainer>

        <ErrorMessage error={error} />

        <Form.ButtonsContainer>
          <Button
            fullWidth
            disabled={isPending}
            type="submit"
            variant="contained"
            size="large"
          >
            {t('users:reset_password.reset')}
          </Button>
          <Form.SecondaryText>
            {t('users:reset_password.remembered_the_password')}{' '}
            <Link component={RouterLink} to={AuthRoutesPaths.Login}>
              {t('users:auth.sign_in')}
            </Link>
          </Form.SecondaryText>
        </Form.ButtonsContainer>
      </Form>
    </>
  );
};
